import React, { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Loader2, ZoomIn } from 'lucide-react';
import { cropImageToFile } from '@/lib/cropImage';

/**
 * The crop step between picking a headshot and it actually uploading (task:
 * headshot crop tool, 2026-09-15). Before this, a rep's photo was center
 * cropped to square automatically, server side, with no way to reposition
 * or zoom before it saved.
 *
 * react-easy-crop (~8KB gzipped, one small dependency) does the actual
 * drag/pinch/zoom math: this is exactly the kind of pointer and gesture
 * handling not worth hand rolling when a small, proven library already
 * solves it.
 *
 * `file` is whatever RepProfileFields.jsx's HeadshotField already has ready
 * to upload (HEIC already converted to JPEG by that point); this component
 * never touches the upload pipeline itself, it only ever hands back an
 * already-cropped square File through onConfirm, same shape as what used to
 * go straight to uploadHeadshot.
 */
export default function HeadshotCropModal({ file, open, onCancel, onConfirm }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!file) {
      setImageSrc(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError('');
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setWorking(true);
    setError('');
    try {
      const cropped = await cropImageToFile(imageSrc, croppedAreaPixels, file?.name || 'headshot.jpg');
      onConfirm(cropped);
    } catch (err) {
      setError(err?.message || 'Could not crop that photo. Try again.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !working) onCancel(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Crop photo</DialogTitle>
          <DialogDescription>Drag to reposition, use the slider to zoom. This is exactly what will be saved.</DialogDescription>
        </DialogHeader>

        {imageSrc && (
          <div className="relative w-full h-64 bg-muted rounded-lg overflow-hidden">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
        )}

        <div className="flex items-center gap-3">
          <ZoomIn className="w-4 h-4 text-muted-foreground shrink-0" />
          <Slider
            value={[zoom]}
            onValueChange={([next]) => setZoom(next)}
            min={1}
            max={3}
            step={0.01}
          />
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={working} className="sm:flex-1">
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={working || !croppedAreaPixels} className="sm:flex-1 bg-accent hover:bg-accent/90 text-accent-foreground">
            {working ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Use this photo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
